import React, { useState } from 'react';
import { useConnection, useWallet } from '@solana/wallet-adapter-react';
import { Transaction, PublicKey, SystemProgram, LAMPORTS_PER_SOL, Connection } from '@solana/web3.js';
import { CreditCard, Plus } from 'lucide-react';
import axios from 'axios';

// Constants
const PAYMENT_WALLET = "3GUcruts9FhzTWXomLRCQozsB5k3tMDkFcH4gmRJJVns";
const TOS_CONTRACT = "HmjCoarLh5duURfJ333DwfFiPyTCgFT35pRSAoP8pump";
const TOS_DECIMALS = 6; // TOS token has 6 decimals

// Use Helius RPC endpoint for mainnet (TOS tokens are on mainnet!)
const HELIUS_RPC = 'https://mainnet.helius-rpc.com/?api-key=39deadfd-d7d3-439b-b247-7de901292884';

// Get backend URL
const getBackendURL = () => {
  return process.env.REACT_APP_BACKEND_URL || 'https://6c1f0f70-88de-468f-b801-d4c2bfd41f7b.preview.emergentagent.com';
};

const BACKEND_URL = getBackendURL();
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

const CreditsManager = ({ userCredits, onPurchase }) => {
  const [showPurchase, setShowPurchase] = useState(false);
  const { connection } = useConnection();
  const { publicKey, sendTransaction, connected } = useWallet();

  const creditPackages = [
    { credits: 100, price_sol: 0.1, price_tos: 50000 }, // 50,000 TOS for 100 credits
    { credits: 500, price_sol: 0.45, price_tos: 200000 }, // 200,000 TOS for 500 credits  
    { credits: 1000, price_sol: 0.85, price_tos: 400000 }, // 400,000 TOS for 1000 credits
  ];

  const handlePurchase = async (packageItem, paymentMethod) => {
    if (!publicKey || !connected) {
      alert('Please connect your wallet first by clicking the "Select Wallet" button in the top right corner');
      return;
    }

    try {
      console.log(`🚀 Starting ${paymentMethod} payment for ${packageItem.credits} credits`);
      console.log(`💳 Package details:`, packageItem);
      console.log(`🔗 Connected wallet:`, publicKey.toString());
      
      let reliableConnection;
      
      if (paymentMethod === 'TOS') {
        // FORCE mainnet RPC for TOS token transactions (TOS tokens are on mainnet!)
        console.log('🌐 TOS payment detected - forcing use of MAINNET RPC');
        console.log('⚠️ TOS tokens exist on MAINNET, not devnet!');
        reliableConnection = new Connection(HELIUS_RPC, 'confirmed');
        
        try {
          await reliableConnection.getLatestBlockhash('confirmed');
          console.log('✅ Connected to Helius MAINNET RPC');
          console.log('🔗 RPC Endpoint:', HELIUS_RPC);
        } catch (heliusError) {
          console.error('❌ Helius mainnet RPC failed:', heliusError);
          throw new Error('❌ Cannot connect to mainnet RPC. TOS tokens are on mainnet, not devnet.');
        }
      } else {
        // For SOL payments, can use default connection
        reliableConnection = connection;
        try {
          await connection.getLatestBlockhash('confirmed');
          console.log('✅ Using default connection for SOL payment');
        } catch (connectionError) {
          console.log('⚠️ Default connection unreliable, using mainnet RPC...');
          reliableConnection = new Connection(HELIUS_RPC, 'confirmed');
          await reliableConnection.getLatestBlockhash('confirmed');
          console.log('✅ Using Helius mainnet RPC for SOL payment');
        }
      }
      
      let transaction = new Transaction();
      let signature;
      
      if (paymentMethod === 'SOL') {
        // SOL payment
        const recipientPubkey = new PublicKey(PAYMENT_WALLET);
        const lamports = Math.floor(packageItem.price_sol * LAMPORTS_PER_SOL);
        
        console.log(`💰 SOL Payment Details:
        - Amount: ${packageItem.price_sol} SOL
        - Lamports: ${lamports}
        - From: ${publicKey.toString()}
        - To: ${PAYMENT_WALLET}
        - Network: ${reliableConnection.rpcEndpoint.includes('mainnet') ? 'MAINNET' : reliableConnection.rpcEndpoint}`);
        
        transaction.add(
          SystemProgram.transfer({
            fromPubkey: publicKey,
            toPubkey: recipientPubkey,
            lamports: lamports,
          })
        );
        
        console.log(`✅ SOL transfer instruction added to transaction`);
        
      } else if (paymentMethod === 'TOS') {
        // TOS token payment - MAINNET ONLY
        const { 
          createTransferCheckedInstruction,
          getAssociatedTokenAddress, 
          createAssociatedTokenAccountInstruction,
          TOKEN_PROGRAM_ID
        } = await import('@solana/spl-token');
        
        const tosMint = new PublicKey(TOS_CONTRACT);
        const recipientPubkey = new PublicKey(PAYMENT_WALLET);
        
        // Calculate amount in smallest units (considering 6 decimals for TOS)
        const amount = Math.floor(packageItem.price_tos * Math.pow(10, TOS_DECIMALS));
        
        console.log(`💰 TOS Payment Details:
        - Amount: ${packageItem.price_tos} TOS
        - Amount (raw): ${amount} (with ${TOS_DECIMALS} decimals)
        - From: ${publicKey.toString()}
        - To: ${PAYMENT_WALLET}
        - Token Mint: ${TOS_CONTRACT}
        - Network: MAINNET (${reliableConnection.rpcEndpoint})
        - ⚠️ CRITICAL: Using MAINNET RPC because TOS tokens are on MAINNET!`);
        
        // Get token accounts - use standard ATA approach
        const sourceTokenAccount = await getAssociatedTokenAddress(
          tosMint,
          publicKey,
          false,
          TOKEN_PROGRAM_ID
        );
        
        const destinationTokenAccount = await getAssociatedTokenAddress(
          tosMint,
          recipientPubkey,
          false,
          TOKEN_PROGRAM_ID
        );
        
        console.log(`🏦 Token Accounts (MAINNET):
        - Source ATA: ${sourceTokenAccount.toString()}
        - Destination ATA: ${destinationTokenAccount.toString()}`);
        
        // Check if destination token account exists, create if needed
        try {
          const destAccountInfo = await reliableConnection.getAccountInfo(destinationTokenAccount);
          if (!destAccountInfo) {
            console.log('🏗️ Creating destination token account on MAINNET...');
            const createDestAccountInstruction = createAssociatedTokenAccountInstruction(
              publicKey, // payer
              destinationTokenAccount, // associated token account
              recipientPubkey, // owner
              tosMint, // mint
              TOKEN_PROGRAM_ID
            );
            transaction.add(createDestAccountInstruction);
          } else {
            console.log('✅ Destination token account already exists on MAINNET');
          }
        } catch (error) {
          console.log('⚠️ Could not check destination account, will try to create:', error.message);
          const createDestAccountInstruction = createAssociatedTokenAccountInstruction(
            publicKey, // payer
            destinationTokenAccount, // associated token account
            recipientPubkey, // owner
            tosMint, // mint
            TOKEN_PROGRAM_ID
          );
          transaction.add(createDestAccountInstruction);
        }
        
        // Create the transfer checked instruction using standard ATA
        const transferInstruction = createTransferCheckedInstruction(
          sourceTokenAccount,        // source token account (standard ATA)
          tosMint,                   // mint
          destinationTokenAccount,   // destination token account
          publicKey,                 // owner of source token account
          amount,                    // amount in smallest units
          TOS_DECIMALS,             // decimals
          [],                       // signers (empty for wallet adapter)
          TOKEN_PROGRAM_ID          // token program
        );
        
        transaction.add(transferInstruction);
        console.log(`✅ TOS transfer checked instruction added to transaction`);
        console.log(`📝 Transfer details: ${packageItem.price_tos} TOS (${amount} raw units with ${TOS_DECIMALS} decimals)`);
        console.log(`📍 Using source account: ${sourceTokenAccount.toString()}`);
        console.log(`🌐 Network: MAINNET (this is where your TOS tokens actually exist!)`);
        
      } else {
        throw new Error(`Payment method ${paymentMethod} not supported yet`);
      }
      
      // Get recent blockhash and set fee payer
      console.log('📡 Getting recent blockhash from MAINNET...');
      const { blockhash } = await reliableConnection.getLatestBlockhash('confirmed');
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = publicKey;
      
      console.log(`🔗 Transaction setup complete:
      - Blockhash: ${blockhash}
      - Fee payer: ${publicKey.toString()}
      - Instructions: ${transaction.instructions.length}
      - Network: ${reliableConnection.rpcEndpoint.includes('mainnet') ? 'MAINNET ✅' : reliableConnection.rpcEndpoint}
      - RPC: ${reliableConnection.rpcEndpoint}`);
      
      // Send transaction using wallet adapter
      console.log('📡 Sending transaction to MAINNET network...');
      try {
        signature = await sendTransaction(transaction, reliableConnection, {
          skipPreflight: false,
          preflightCommitment: 'confirmed',
          maxRetries: 3
        });
        console.log('✅ Transaction sent successfully to MAINNET, signature:', signature);
        console.log(`🔗 Check transaction on Solana Explorer: https://explorer.solana.com/tx/${signature}`);
        console.log(`🌐 This transaction should now appear on MAINNET Solana Explorer!`);
        
      } catch (sendError) {
        console.error('❌ Failed to send transaction to MAINNET:', sendError);
        
        // Provide more specific error messages
        if (sendError.message.includes('User rejected')) {
          throw new Error('❌ Transaction was cancelled by user');
        } else if (sendError.message.includes('insufficient funds') || sendError.message.includes('0x1')) {
          if (paymentMethod === 'SOL') {
            throw new Error(`❌ Insufficient SOL balance. You need ${packageItem.price_sol} SOL plus ~0.001 SOL for transaction fees.`);
          } else {
            throw new Error(`❌ Insufficient TOS tokens or SOL for transaction fees. You need ${packageItem.price_tos} TOS plus ~0.001 SOL for fees.`);
          }
        } else if (sendError.message.includes('TokenAccountNotFoundError') || sendError.message.includes('AccountNotFound')) {
          throw new Error(`❌ TOS token account not found on MAINNET.\n\nThis could mean:\n1. You don't have TOS tokens in your Associated Token Account\n2. Your TOS tokens are in a different account format\n3. The token account needs to be created\n\nTOS Contract: ${TOS_CONTRACT}`);
        } else {
          throw new Error(`❌ Transaction failed on MAINNET: ${sendError.message}`);
        }
      }
      
      // Wait for confirmation
      console.log('⏳ Waiting for transaction confirmation on MAINNET...');
      const timeoutMs = paymentMethod === 'TOS' ? 120000 : 60000; // 2 minutes for TOS, 1 minute for SOL
      
      try {
        const confirmation = await Promise.race([
          reliableConnection.confirmTransaction(signature, 'confirmed'),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Transaction confirmation timeout')), timeoutMs)
          )
        ]);
        
        if (confirmation.value.err) {
          throw new Error(`❌ Transaction failed on MAINNET blockchain: ${JSON.stringify(confirmation.value.err)}`);
        }
        
        console.log('✅ Transaction confirmed successfully on MAINNET blockchain');
        
      } catch (error) {
        if (error.message.includes('timeout')) {
          console.log('Confirmation timeout - checking transaction status...');
          
          // Wait a bit more and try to get transaction status
          await new Promise(resolve => setTimeout(resolve, 5000));
          
          try {
            const txStatus = await reliableConnection.getSignatureStatus(signature);
            if (txStatus.value?.confirmationStatus === 'confirmed' || txStatus.value?.confirmationStatus === 'finalized') {
              console.log('✅ Transaction was actually confirmed on MAINNET despite timeout');
            } else if (txStatus.value?.err) {
              throw new Error(`❌ Transaction failed on MAINNET blockchain: ${JSON.stringify(txStatus.value.err)}`);
            } else {
              // Transaction might still be processing, let's continue
              console.log('⏰ Transaction still processing on MAINNET, continuing with backend recording...');
            }
          } catch (statusError) {
            console.log('⚠️ Could not check transaction status, continuing anyway...');
          }
        } else {
          throw error;
        }
      }
      
      // Record purchase on backend
      console.log('📡 Recording purchase on backend...');
      try {
        const response = await axios.post(`${API}/purchase-credits`, {
          wallet_address: publicKey.toString(),
          credits_amount: packageItem.credits,
          transaction_signature: signature,
          payment_method: paymentMethod === 'SOL' ? 'SOL' : 'TOS_TOKEN',
          amount: paymentMethod === 'SOL' ? packageItem.price_sol : packageItem.price_tos
        });

        console.log('✅ Purchase recorded on backend:', response.data);
        
        // Refresh user credits
        onPurchase();
        setShowPurchase(false);
        
        alert(`🎉 Successfully purchased ${packageItem.credits} credits!\n\nTransaction: ${signature}\n\nYour credits have been added to your account.\n\nView on Solana Explorer: https://explorer.solana.com/tx/${signature}\n\n✅ Transaction confirmed on MAINNET!`);
        
      } catch (backendError) {
        console.error('❌ Backend recording failed:', backendError);
        
        // Still show success but with warning about backend
        alert(`⚠️ Transaction successful on MAINNET but backend recording failed!\n\nTransaction: ${signature}\n\nYour transaction is confirmed on MAINNET blockchain. Please contact support if credits don't appear.\n\nView on Solana Explorer: https://explorer.solana.com/tx/${signature}`);
      }

    } catch (error) {
      console.error('❌ Purchase failed:', error);
      let errorMessage = 'Purchase failed';
      
      if (error.message?.includes('User rejected') || error.message?.includes('cancelled')) {
        errorMessage = 'Transaction was cancelled by user';
      } else if (error.message?.includes('TOS token account not found') ||
                 error.message?.includes('Insufficient TOS tokens') ||
                 error.message?.includes('Transaction failed')) {
        errorMessage = error.message; // Use the detailed error
      } else if (error.message?.includes('insufficient funds') || error.message?.includes('Insufficient SOL')) {
        errorMessage = error.message;
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert(`❌ ${errorMessage}`);
    }
  };

  return (
    <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <CreditCard className="h-5 w-5 text-purple-400" />
          <h3 className="text-lg font-semibold text-white">Credits</h3>
        </div>
        <div className="flex items-center space-x-4">
          <span className="text-2xl font-bold text-purple-400">{userCredits}</span>
          <button
            onClick={() => setShowPurchase(true)}
            className="px-4 py-2 bg-gradient-to-r from-purple-500 to-violet-500 text-white rounded-lg hover:opacity-90 transition-all"
          >
            <Plus className="h-4 w-4 mr-1 inline" />
            Buy Credits
          </button>
        </div>
      </div>

      {showPurchase && (
        <div className="space-y-4">
          <h4 className="font-semibold text-white">Select Credit Package</h4>
          <div className="grid gap-4">
            {creditPackages.map((pkg, index) => (
              <div key={index} className="bg-black/20 rounded-lg p-4 border border-gray-700">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-lg font-semibold text-white">{pkg.credits} Credits</span>
                  <div className="text-sm text-gray-400">
                    <div>{pkg.price_sol} SOL</div>
                    <div>{(pkg.price_tos / 1000).toFixed(0)}K TOS</div>
                  </div>
                </div>
                <div className="space-y-2">
                  <button
                    onClick={() => handlePurchase(pkg, 'SOL')}
                    className="w-full px-3 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                  >
                    Pay {pkg.price_sol} SOL
                  </button>
                  <button
                    onClick={() => handlePurchase(pkg, 'TOS')}
                    className="w-full px-3 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded text-sm hover:opacity-90"
                  >
                    Pay {(pkg.price_tos / 1000).toFixed(0)}K TOS
                  </button>
                </div>
              </div>
            ))}
          </div>
          <button
            onClick={() => setShowPurchase(false)}
            className="w-full px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

export default CreditsManager;